import { useEffect, useState } from "react";
import { useDevice } from "../../hooks/useDevice";
import { CustomDropdownBoxButton } from "../button/CustomDropdownBoxButton";
import { Column, Padding, Row, SizedBox } from "../CommonBaseComponents";
import { C1Text, C2Text, T1Text } from "../text/CustomText";
import CustomBarGraphChart from "../chart/CustomBarGraphChart";
import CustomLineGraphChart from "../chart/CustomLineGraphChart";
import CustomPeriodButton from "../button/CustomPeriodButton";
import { defaultGraphKeys } from "../chart/CustomGrpahCommonComponents";
import Translate from "../../translate/Translate";
import { Theme } from "../../Theme";

export const defaultButtonSectionValue = [
    { value: "Daily", label: Translate("common.daily") },
    { value: "Weekly", label: Translate("common.weekly") },
    { value: "Monthly", label: Translate("common.monthly") },
];

export const weekdayButtonSectionValue = [
    { value: "Date", label: "Date" },
    { value: "Weekdays", label: "Weekdays" },
];

const CustomGraphCard = ({
    dailyData,
    weeklyData,
    monthlyData,
    customData,
    title,
    mainText,
    subText,
    isBarChart,
    hasButtonSection = true,
    keys = defaultGraphKeys,
    hasLegend = true,
    isMainTextLegend = false,
    hasBarLabel = false,
    buttonSectionValue = defaultButtonSectionValue,
    customTooltip,
    referenceAreaFillColor,
    onDateRangeChange,
}) => {
    const currentDevice = useDevice();
    const [dateRange, setDateRange] = useState(buttonSectionValue[0].value);
    const [data, setData] = useState([]);

    // 선택된 기간 탭을 부모에 알려 mainText 등을 탭에 맞춰 갱신할 수 있게 함
    useEffect(() => {
        onDateRangeChange?.(dateRange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange]);

    useEffect(() => {
        if (hasButtonSection) {
            if (dateRange === "Daily") {
                setData(dailyData);
            } else if (dateRange === "Weekly") {
                setData(weeklyData);
            } else if (dateRange === "Monthly") {
                setData(monthlyData);
            } else if (dateRange === "Custom") {
                setData(customData);
            } else if (dateRange === "Date") {
                setData(dailyData);
            } else if (dateRange === "Weekdays") {
                setData(weeklyData);
            }
        } else {
            setData(customData);
        }
    }, [weeklyData, monthlyData, dailyData, customData, dateRange, hasButtonSection]);

    let buttonSection;

    if (currentDevice === "mobile") {
        buttonSection = (
            <CustomDropdownBoxButton
                icon={"date"}
                currentValue={dateRange}
                setValue={setDateRange}
                values={buttonSectionValue}
            />
        );
    } else {
        buttonSection = (
            <CustomPeriodButton currentValue={dateRange} setValue={setDateRange} values={buttonSectionValue} />
        );
    }

    const totals =
        isMainTextLegend && customData && customData.length > 0
            ? customData.reduce((acc, day) => {
                  Object.keys(day).forEach((key) => {
                      if (key !== "name") {
                          acc[key] = (acc[key] || 0) + day[key];
                      }
                  });
                  return acc;
              }, {})
            : {};

    return (
        <SizedBox
            border={`1px solid ${Theme.color.gray250}`}
            borderRadius={"16px"}
            width={"100%"}
            height={"100%"}
            backgroundColor={"transparent"}
            padding={"20px"}
            justifyContent={"flex-start"}
            alignItem={"flex-start"}
        >
            <Column width={"100%"}>
                <Row justifyContent={"space-between"} alignItem={"flex-start"} width={"100%"}>
                    <Column alignItem={"flex-start"}>
                        <C1Text>{title}</C1Text>
                        <Row alignItem={"flex-end"} width={"100%"}>
                            {mainText && <T1Text>{mainText}</T1Text>}
                            {isMainTextLegend &&
                                keys.map((entry, index) => (
                                    <div
                                        key={`item-${index}`}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            marginRight: "24px",
                                            marginTop: "8px",
                                        }}
                                    >
                                        {/* 1. 색상 막대 */}
                                        <div
                                            style={{
                                                width: "3px",
                                                height: "32px",
                                                backgroundColor: entry.color,
                                                borderRadius: "3px",
                                            }}
                                        />
                                        {/* 2. 텍스트 (이름 + 값) */}
                                        <div>
                                            <T1Text
                                                fontColor={Theme.color.zinc850}
                                                fontWeight={"600"}
                                                style={{ marginRight: "6px" }}
                                            >
                                                {entry.currency ?? ""}
                                                {(totals[entry.name] || 0).toLocaleString()}
                                            </T1Text>
                                            <C1Text fontColor={Theme.color.gray500}>{entry.label}</C1Text>
                                        </div>
                                    </div>
                                ))}
                            <SizedBox width={"8px"} />
                            {subText && (
                                <Padding padding={"6px 0"}>
                                    <C2Text fontColor={Theme.color.gray500}>{subText}</C2Text>
                                </Padding>
                            )}
                        </Row>
                    </Column>
                    {hasButtonSection && buttonSection}
                </Row>
                <SizedBox height={"16px"} />
                {isBarChart ? (
                    <CustomBarGraphChart
                        data={data}
                        keys={keys}
                        hasLegend={hasLegend}
                        hasBarLabel={hasBarLabel}
                        customTooltip={customTooltip}
                        referenceAreaFillColor={referenceAreaFillColor}
                    />
                ) : (
                    <CustomLineGraphChart data={data} hasLegend={hasLegend} hasBarLabel={hasBarLabel} />
                )}
            </Column>
        </SizedBox>
    );
};

export default CustomGraphCard;
